import { useWsEvents } from '../hooks/useWsEvents';
import { ScoreBadge, PlayerInfoBlock, useScorePulse } from '../components/PlayerWidgetParts';
import type { PlayerWidgetData, PlayerPhotoData } from '../types/widgets';
import { fonts, glass, glassBorder, glassText, brandGradient } from '../styles/overlayTokens';

const INITIAL_PLAYER: PlayerWidgetData = {
  p1: { name: 'Player 1', archetype: '' },
  p2: { name: 'Player 2', archetype: '' },
  p1Score: 0,
  p2Score: 0,
  bestOf: 3,
  turnIndicator: null,
  p1Identity: null,
  p2Identity: null,
};

const INITIAL_PHOTO: PlayerPhotoData = {};

// Preset packs
const PRESET_PLAYER_ENTRY: Record<string, string> = {
  clean: 'wipe-top',
  dynamic: 'slide-opponents',
  dramatic: 'expand-center',
  retro: 'fade-stagger',
  classic: 'fade-stagger',
};

export function PlayerWidget() {
  const params = new URLSearchParams(window.location.search);
  const useFallback = params.get('fallback') === 'true';

  const { data, animationSettings } = useWsEvents<PlayerWidgetData>('playerWidget', INITIAL_PLAYER);
  const { data: photoData } = useWsEvents<PlayerPhotoData>('playerPhoto', INITIAL_PHOTO);

  const showPhotos = data.showPhotos ?? true;
  const showHealth = data.showHealth ?? true;
  const showScores = data.showScores ?? true;
  const avatarShape = data.avatarShape;

  const paddingMap = { center: '0 200px', 'three-quarter': '0 120px', edge: '0 24px' } as const;
  const playerPadding = paddingMap[data.playerWidgetAlign ?? 'edge'];

  // Score change animation
  const p1ScoreAnim = useScorePulse(data.p1Score);
  const p2ScoreAnim = useScorePulse(data.p2Score);

  const hc = data.healthConfig;
  const hcs = data.healthConfigs;
  const p1Leading = data.p1Score > data.p2Score;
  const p2Leading = data.p2Score > data.p1Score;

  // Resolve entry animation
  const entryPreset = animationSettings?.preset ?? 'dynamic';
  const entryId = animationSettings?.mode === 'custom'
    ? animationSettings.playerEntry ?? 'slide-opponents'
    : PRESET_PLAYER_ENTRY[entryPreset] ?? 'slide-opponents';

  // Section animation classes
  const isSlideOpponents = entryId === 'slide-opponents';
  const isFadeStagger = entryId === 'fade-stagger';
  const leftAnim = isSlideOpponents ? 'anim-slide-opponents-left' : isFadeStagger ? 'anim-fade-stagger-1' : `anim-${entryId}`;
  const centerAnim = isSlideOpponents ? 'anim-slide-opponents-center' : isFadeStagger ? 'anim-fade-stagger-2' : `anim-${entryId}`;
  const rightAnim = isSlideOpponents ? 'anim-slide-opponents-right' : isFadeStagger ? 'anim-fade-stagger-3' : `anim-${entryId}`;

  const bgStyle = useFallback
    ? { background: '#121216' }
    : { background: glass.surfaceHeavy, backdropFilter: 'blur(16px) saturate(1.2)' };

  const turnActive = data.turnIndicator;
  const turnLabel = turnActive === 'p1'
    ? `${data.p1.name || 'P1'}'s Turn`
    : turnActive === 'p2'
      ? `${data.p2.name || 'P2'}'s Turn`
      : null;

  return (
    <div style={{
      width: 1920,
      height: 180,
      display: 'flex',
      background: 'transparent',
      fontFamily: fonts.body,
      color: glassText.primary,
    }}>
      <div style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        ...bgStyle,
        borderRadius: 12,
        border: `1px solid ${glassBorder.light}`,
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        overflow: 'hidden',
      }}>
        {/* Bottom accent bar */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 2,
          background: brandGradient.horizontal,
        }} />

        {/* Left section — Player 1 */}
        <div className={leftAnim} style={{
          flex: '1 1 640px',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          padding: playerPadding,
          minWidth: 0,
        }}>
          <PlayerInfoBlock
            name={data.p1.name}
            archetype={data.p1.archetype}
            record={data.p1Record}
            photoUrl={photoData.p1Photo}
            showPhoto={showPhotos}
            glowActive={turnActive === 'p1'}
            health={data.p1Health}
            healthStage={(data.p1HealthStage ?? 1) as 1 | 2}
            healthConfig={hc}
            showHealth={showHealth}
            align="left"
            avatarShape={avatarShape}
            avatarSize={128}
            inlineHealth
            securitySize={96}
            healthConfigs={hcs}
            healths={data.p1Healths}
            healthStages={data.p1HealthStages}
            healthMaxes={data.p1HealthMaxes}
            pilotCardUrl={data.p1.pilotCardUrl}
            coreCardUrl={data.p1.coreCardUrl}
            thumbSize="standard"
          />
        </div>

        {/* Center section — Score */}
        <div className={centerAnim} style={{
          flex: '0 0 auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 24px',
          gap: 4,
          minWidth: 180,
        }}>
          {showScores ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className={p1ScoreAnim}><ScoreBadge score={data.p1Score} isLeading={p1Leading} /></div>
                <span style={{ fontSize: 20, fontWeight: 600, color: glassText.muted }}>VS</span>
                <div className={p2ScoreAnim}><ScoreBadge score={data.p2Score} isLeading={p2Leading} /></div>
              </div>
              <div style={{ fontSize: 14, color: glassText.secondary }}>
                Best of {data.bestOf}
              </div>
              {turnLabel && (
                <div style={{ fontSize: 14, color: '#FF9570', fontWeight: 600 }}>
                  {turnLabel}
                </div>
              )}
            </>
          ) : (
            // Scores hidden — show just "VS" in the center
            <span style={{ fontSize: 20, fontWeight: 600, color: glassText.muted }}>VS</span>
          )}
        </div>

        {/* Right section — Player 2 (mirrored) */}
        <div className={rightAnim} style={{
          flex: '1 1 640px',
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row-reverse',
          gap: 16,
          padding: playerPadding,
          minWidth: 0,
        }}>
          <PlayerInfoBlock
            name={data.p2.name}
            archetype={data.p2.archetype}
            record={data.p2Record}
            photoUrl={photoData.p2Photo}
            showPhoto={showPhotos}
            glowActive={turnActive === 'p2'}
            health={data.p2Health}
            healthStage={(data.p2HealthStage ?? 1) as 1 | 2}
            healthConfig={hc}
            showHealth={showHealth}
            align="right"
            avatarShape={avatarShape}
            avatarSize={128}
            inlineHealth
            securitySize={96}
            healthConfigs={hcs}
            healths={data.p2Healths}
            healthStages={data.p2HealthStages}
            healthMaxes={data.p2HealthMaxes}
            pilotCardUrl={data.p2.pilotCardUrl}
            coreCardUrl={data.p2.coreCardUrl}
            thumbSize="standard"
            mirrorAvatarStack
          />
        </div>
      </div>
    </div>
  );
}
